import { otpChannel, requestWithOtpSecurity, type OtpChallenge } from "@templeos/shared/otp-browser";
import { useOtpFlow } from "@templeos/shared/otp-react";
import { countDenominations, DENOMINATIONS, formatINR } from "@templeos/shared";
import { Alert, Button, Card, PageHeader, SelectField, TextAreaField, TextField, useToast } from "@templeos/ui";
import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { Shell } from "../components/Shell.js";
import { ShiftSummaryView, useShiftTally } from "../components/ShiftPanel.js";
import { api, ApiError, errorText } from "../lib/api.js";
import { handoverSlipJob, printJobs, printProblem, type HandoverData } from "../lib/print.js";
import { engine, useSession } from "../session.js";

/** Shift close with denomination count, variance explanation and supervisor approval (core close endpoint). */
export function ShiftClose() {
  const s = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const { t, sales } = useShiftTally();
  const refused = sales.filter((x) => x.status === "rejected" && !x.voided && x.payments.some((p) => p.mode === "cash"));
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [explanation, setExplanation] = useState("");
  const [sup, setSup] = useState({ username: "", password: "" });
  const [error, setError] = useState<string | null>(null);
  const [closed, setClosed] = useState<HandoverData | null>(null);
  const [printStatus, setPrintStatus] = useState("");
  const [printing, setPrinting] = useState(false);
  const printInFlight = useRef(false);

  const printHandover = async (data: HandoverData) => {
    if (printInFlight.current) return;
    printInFlight.current = true;
    setPrinting(true);
    setPrintStatus("Preparing the handover slip. Your shift closure is already saved.");
    try {
      const outcome = await printJobs(s.config?.printerUrl ?? null, [handoverSlipJob(data, s.slip)]);
      const problem = printProblem(outcome);
      setPrintStatus(problem ? `${problem}. Check the printer before requesting another copy.` : outcome.via === "browser"
        ? "Browser print dialog requested. Confirm printing there and check the handover slip."
        : "Handover slip sent to the printer. Check the printed slip before handing over the cash.");
    } catch (failure) {
      setPrintStatus(`Could not finish the print request: ${errorText(failure)}. Your shift remains closed.`);
    } finally {
      printInFlight.current = false;
      setPrinting(false);
    }
  };

  const denominations = Object.fromEntries(Object.entries(counts).map(([k, v]) => [k, Number(v || 0)]).filter(([, v]) => Number(v) > 0)) as Record<string, number>;
  const countBad = (v: string) => v !== "" && !/^\d{1,6}$/.test(v);
  const badCount = Object.values(counts).some(countBad);
  const counted = badCount ? 0 : countDenominations(denominations);
  const expected = s.shift?.expectedCash ?? 0;
  const variance = counted - expected;

  const body = { denominations, explanation: variance !== 0 ? explanation.trim() : null,
    supervisor: variance !== 0 ? { username: sup.username.trim(), password: sup.password } : undefined };
  const otp = useOtpFlow({
    fallback: async (challenge) => api.post<OtpChallenge>(`/api/core/shifts/${s.shift?.shift.id}/otp/fallback`, {
      ...body, challengeId: challenge.challengeId,
    }, 45_000),
    errorMessage: errorText,
  });
  const busy = otp.busy;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!s.shift || busy) return;
    if (badCount) return setError("Counts must be whole numbers.");
    if (variance !== 0 && explanation.trim().length < 10) return setError("Explain the cash difference (at least 10 characters).");
    if (variance !== 0 && (!sup.username.trim() || !sup.password)) return setError("A supervisor must approve the cash difference with their username and password.");
    setError(null);
    const shift = s.shift;
    await otp.perform(async (signal) => {
      let r: { expected: number; counted: number; variance: number };
      try {
        r = await api.post<typeof r>(`/api/core/shifts/${shift.shift.id}/close`, {
          ...body, otp: otp.challenge ? { challengeId: otp.challenge.challengeId, code: otp.code } : undefined,
        }, 45_000);
      } catch (failure) {
        if (!otp.challenge && failure instanceof ApiError && failure.code === "OTP_REQUIRED") {
          const challenge = await requestWithOtpSecurity((recaptchaToken) => api.post<OtpChallenge>(`/api/core/shifts/${shift.shift.id}/otp`, { ...body, recaptchaToken }, 45_000), signal);
          return { kind: "challenge" as const, challenge };
        }
        throw failure;
      }
      const data: HandoverData = {
        openedAt: shift.shift.opened_at, openingFloat: shift.shift.opening_float, expected: r.expected, counted: r.counted, variance: r.variance,
        explanation: r.variance ? explanation.trim() : null, denominations, receipts: shift.counts.issued, byMode: shift.byMode.map((m) => ({ mode: m.mode, amount: m.amount })),
        approvedBy: r.variance ? sup.username.trim() : null,
      };
      return { kind: "closed" as const, data };
    }, (result) => {
      if (result.kind === "challenge") otp.accept(result.challenge);
      else {
        // The API has committed. Printer/dialog delays must never leave this successful closure looking pending.
        otp.clear(); setSup({ username: "", password: "" }); setClosed(result.data);
        toast("Shift closed. The cash count and handover are saved.", "success");
        void s.refreshShift().catch(() => toast("Shift closed, but the latest shift status could not be refreshed. Reload before opening another shift.", "danger"));
        void printHandover(result.data);
      }
    });
  };

  const paper = (
    <SelectField label="Receipt paper" value={String(s.paperWidth)} onChange={(e) => s.setPaperWidth(e.target.value === "32" ? 32 : 42)}
      options={[{ value: "42", label: "80 mm roll" }, { value: "32", label: "58 mm roll" }]} />
  );

  if (closed) {
    return (
      <Shell>
        <div className="pos-page">
          <PageHeader title="Shift closed" subtitle="The cash count and handover are saved. Printing is shown separately below." />
          <Card>
            <p role="status">{printStatus}</p>
            <div className="tos-grid">
              <div><div className="tos-stat-label">Expected</div><div className="pos-variance">{formatINR(closed.expected)}</div></div>
              <div><div className="tos-stat-label">Counted</div><div className="pos-variance">{formatINR(closed.counted)}</div></div>
              <div><div className="tos-stat-label">Variance</div><div className={`pos-variance ${closed.variance ? "off" : "zero"}`}>{formatINR(closed.variance)}</div></div>
            </div>
            <div className="tos-form-actions">
              <Button loading={printing} disabled={printing} onClick={() => void printHandover(closed)}>Print slip again</Button>
              <Button onClick={() => navigate("/")}>Open a new shift</Button>
              <Button variant="primary" onClick={() => void s.logout()}>Sign out</Button>
            </div>
          </Card>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="pos-page">
        <PageHeader title="Shift & cash close" subtitle={s.me?.counter ? `${s.me.counter.code} · ${s.me.counter.name}` : undefined}
          actions={<Button onClick={() => void s.refreshShift()}>Refresh figures</Button>} />
        <div className="tos-stack">
          <ShiftSummaryView />
          <div style={{ maxWidth: 260 }}>{paper}</div>
          {s.shift && (
            <Card title="Count the drawer">
              <form className="tos-stack" onSubmit={submit} noValidate>
                {t.pending > 0 && (
                  <Alert tone="warning">
                    {t.pending} bill(s) are still on this terminal only. Sync them before closing so the expected cash is right.{" "}
                    <Button size="sm" onClick={() => void engine.kick()}>Sync now</Button>
                  </Alert>
                )}
                {!s.reachable && <Alert tone="danger">The server is unreachable. A shift can only be closed online.</Alert>}
                {refused.length > 0 && (
                  <Alert tone="warning">
                    {refused.length} cash bill(s) worth {formatINR(refused.reduce((sum, x) => sum + x.total, 0))} were refused by the server and are not in
                    the expected cash. If that money was not returned to the devotees it is still in the drawer: count it, and explain the
                    difference for the supervisor.
                  </Alert>
                )}
                <div className="pos-denoms">
                  {DENOMINATIONS.map((d) => {
                    const id = `den-${d.paise}`;
                    const v = counts[String(d.paise)] ?? "";
                    return (
                      <div key={d.paise} className="pos-denom">
                        <label htmlFor={id}>{d.label}</label>
                        <input id={id} className="tos-input tos-num" inputMode="numeric" value={v} placeholder="0" aria-describedby={`${id}-sum`}
                          aria-invalid={countBad(v) || undefined} disabled={busy} onChange={(e) => { otp.clear(); setCounts({ ...counts, [String(d.paise)]: e.target.value.trim() }); }} />
                        <span id={`${id}-sum`} className={countBad(v) ? "tos-error" : "tos-muted tos-num"} style={{ gridColumn: "2" }}>
                          {countBad(v) ? "Whole number of notes" : formatINR(d.paise * Number(/^\d+$/.test(v) ? v : 0))}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="tos-grid" aria-live="polite">
                  <div><div className="tos-stat-label">Counted</div><div className="pos-variance">{formatINR(counted)}</div></div>
                  <div><div className="tos-stat-label">Expected (server)</div><div className="pos-variance">{formatINR(expected)}</div></div>
                  <div><div className="tos-stat-label">Variance</div><div className={`pos-variance ${variance ? "off" : "zero"}`}>{variance > 0 ? "+" : ""}{formatINR(variance)}</div></div>
                </div>
                {variance !== 0 && (
                  <>
                    <TextAreaField label="Explanation of the difference" required value={explanation} maxLength={500} disabled={busy} onChange={(e) => { otp.clear(); setExplanation(e.target.value); }}
                      hint="At least 10 characters; the supervisor approves it below." />
                    <div className="tos-form-grid">
                      <TextField label="Supervisor username" required autoComplete="off" value={sup.username} disabled={busy} onChange={(e) => { otp.clear(); setSup({ ...sup, username: e.target.value }); }} />
                      <TextField label="Supervisor password" required type="password" autoComplete="off" value={sup.password} disabled={busy} onChange={(e) => { otp.clear(); setSup({ ...sup, password: e.target.value }); }} />
                    </div>
                  </>
                )}
                {otp.challenge && <>
                  <Alert tone="info">Enter the code sent to {otp.challenge.phoneMasked ?? "your registered mobile"} to confirm this cash count.
                    {otpChannel(otp.challenge) === "whatsapp" ? " Check WhatsApp." : " Check your SMS messages."} Use the newest code after delivery switches.
                    {otp.challenge.devCode && <> Demo code: <strong>{otp.challenge.devCode}</strong>.</>}
                  </Alert>
                  <TextField label="Shift-close verification code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp.code} disabled={busy}
                    onChange={(event) => otp.setCode(event.target.value.replace(/\D/g, ""))} />
                  {typeof otp.challenge.fallbackAfterSeconds === "number" && <Button disabled={busy} onClick={() => void otp.fallback()}>Code delayed? Try another delivery method</Button>}
                  <Button disabled={busy} onClick={otp.clear}>Restart verification</Button>
                </>}
                {(error || otp.error) && <Alert tone="danger">{error || otp.error}</Alert>}
                <div className="tos-form-actions">
                  <Button type="submit" variant="primary" size="lg" loading={busy} disabled={t.pending > 0 || !s.reachable}>{otp.challenge ? "Confirm code, close shift and print" : "Send shift-close verification code"}</Button>
                </div>
              </form>
            </Card>
          )}
        </div>
      </div>
    </Shell>
  );
}
